/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import espInfo, { EspKey } from 'cio/utils/email-service-providers';
import { task } from 'ember-concurrency';

import DS from 'ember-data';
import MailServerModel from 'cio/models/mail-server';
import { EspOption } from 'cio/components/custom-smtp-settings/create-server-modal/steps/provider';
import AnalyticsService from 'cio/services/analytics';
import { CustomSmtpSetupAction, CustomSmtpSetupStep } from 'cio/utils/analytics/transforms/email';

enum CreateStep {
  Provider = 'provider',
  Setup = 'setup',
  Success = 'success',
}

export enum SetupSubStep {
  Webhooks = 'webhooks',
  Credentials = 'credentials',
}

interface CreateServerModalArgs {
  onClose: () => unknown;
  onSave: (server: MailServerModel) => unknown;
}

export default class CreateServerModal extends Component<CreateServerModalArgs> {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked confirmedWebhookSetup = false;
  @tracked currentStepNumber = 0;
  @tracked server: MailServerModel;
  @tracked selectedEsp?: EspKey;

  @tracked currentSetupSubStep: SetupSubStep | null = null;

  readonly steps = [CreateStep.Provider, CreateStep.Setup, CreateStep.Success];

  get selectedEspInfo() {
    return this.selectedEsp ? espInfo(this.selectedEsp) : undefined;
  }
  get currentStep() {
    return this.steps[this.currentStepNumber];
  }

  constructor(owner: unknown, args: CreateServerModalArgs) {
    super(owner, args);

    this.server = this.store.createRecord('mail-server');
  }

  @action
  continue() {
    if (this.currentStepNumber < this.steps.length) {
      if (this.currentStepNumber === 0 && this.selectedEspInfo?.supportsWebhooks) {
        this.analytics.track('email', 'custom_smtp_add_server_setup_action_complete', {
          action: CustomSmtpSetupAction.ContinuedToSetup,
          esp: this.selectedEsp,
        });
      } else if (this.currentStepNumber === 1) {
        this.analytics.track('email', 'custom_smtp_add_server_setup_action_complete', {
          action: CustomSmtpSetupAction.AddedCredentials,
          esp: this.selectedEsp,
        });
      }

      this.currentStepNumber = this.currentStepNumber + 1;

      if (this.currentStepNumber !== this.steps.indexOf(CreateStep.Setup)) {
        this.currentSetupSubStep = null;
      }
    }
  }
  @action
  goBack() {
    if (this.currentStepNumber > 0) {
      this.currentStepNumber = this.currentStepNumber - 1;

      if (this.currentStepNumber !== this.steps.indexOf(CreateStep.Setup)) {
        this.currentSetupSubStep = null;
      }
    }
  }

  @action
  cancel() {
    if (this.currentStepNumber === this.steps.indexOf(CreateStep.Provider)) {
      this.analytics.track('email', 'custom_smtp_add_server_setup_canceled', {
        step: CustomSmtpSetupStep.ProviderSelection,
        esp: this.selectedEsp,
      });
    } else if (this.currentStepNumber === this.steps.indexOf(CreateStep.Setup) && this.currentSetupSubStep != null) {
      let step = {
        [SetupSubStep.Webhooks]: CustomSmtpSetupStep.SetupWebhooks,
        [SetupSubStep.Credentials]: CustomSmtpSetupStep.AddCredentials,
      }[this.currentSetupSubStep];

      if (step != null) {
        this.analytics.track('email', 'custom_smtp_add_server_setup_canceled', {
          step,
          esp: this.selectedEsp,
        });
      }
    } else if (this.currentStepNumber === this.steps.indexOf(CreateStep.Success)) {
      this.analytics.track('email', 'custom_smtp_add_server_setup_action_complete', {
        action: CustomSmtpSetupAction.FinishedSetup,
        esp: this.selectedEsp,
      });
    }
    this.server.rollbackAttributes();
    this.args.onClose();
  }

  @action
  selectEsp({ value }: EspOption) {
    this.confirmedWebhookSetup = false;
    this.selectedEsp = value;

    let isSendgrid = value === 'sendgrid';

    let address = this.selectedEspInfo?.address;

    if (typeof address === 'object' && address.default != null) {
      address = address.default;
    }

    this.server.setProperties({
      authentication: this.selectedEspInfo?.authentication,
      useApi: isSendgrid,
      password: undefined,
      address,
      port: this.selectedEspInfo?.port,
    });

    this.analytics.track('email', 'custom_smtp_add_server_setup_action_complete', {
      action: CustomSmtpSetupAction.DeliveryProviderSelected,
      esp: value,
    });
  }

  saveTask = task({ drop: true }, async () => {
    let response = await this.args.onSave(this.server);
    this.continue();

    return response;
  });

  @action
  setSetupSubStep(subStep: SetupSubStep | null) {
    this.currentSetupSubStep = subStep;

    if (subStep === SetupSubStep.Credentials) {
      this.analytics.track('email', 'custom_smtp_add_server_setup_action_complete', {
        action:
          this.confirmedWebhookSetup || this.selectedEsp === EspKey.Other
            ? CustomSmtpSetupAction.ContinuedToAddCredentials
            : CustomSmtpSetupAction.SkippedToAddCredentials,
        esp: this.selectedEsp,
      });
    }
  }
}
